import React, { useState } from 'react';
import { FormControl, InputLabel, Select, MenuItem, FormHelperText, TextField, Button, Typography, Box, Backdrop, CircularProgress } from '@mui/material';
import { useAlert } from '../../contexts/AlertContext';
import { usePresale } from '../../contexts/PresaleContext';

const PrivatePoolParticipantForm = () => {
    const { showAlert } = useAlert();
    const { isModalLoading, addPrivateSubmission, privatePools } = usePresale();

    const [form, setForm] = useState({
        firstName: '',
        lastName: '',
        poolName: '',
        purchaseAmount: '',
        twitter: '',
        company: '',
        email: '',
        message: '',
    });

    const requiredFields = ['firstName', 'lastName', 'poolName', 'purchaseAmount', 'twitter'];

    const [errors, setErrors] = useState({});

    const handleChange = (field) => (e) => {
        setForm({ ...form, [field]: e.target.value });
        if (e.target.value) {
            setErrors({ ...errors, [field]: false });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = {};
        let isValid = true;

        requiredFields.forEach((field) => {
            if (!form[field]) {
                newErrors[field] = true;
                isValid = false;
            }
        });

        setErrors(newErrors);

        if (isValid) {
            const response = await addPrivateSubmission(form);
            if (response.success) {
                showAlert(`Successfully submitted your request to join the pool!`, 'success');
                setForm({
                    firstName: '',
                    lastName: '',
                    poolName: '',
                    purchaseAmount: '',
                    twitter: '',
                    company: '',
                    email: '',
                    message: '',
                });
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                showAlert(`Submission failed. Please try again later.`, 'error');
            }
        }
    };

    const fieldProps = {
        fullWidth: true,
        sx: {
            mb: 3,
            backgroundColor: '#1e1e1e',
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#749d1a' },
        },
        InputLabelProps: { style: { color: '#ccc' } },
        InputProps: { style: { color: '#fff' } },
        FormHelperTextProps: { style: { color: '#efbc00' } },
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ color: '#fff', backgroundColor: '#121212', p: 4, borderRadius: 4, maxWidth: 720, mx: 'auto', my: 5 }}>
            <Typography variant="h4" sx={{ color: '#efbc00', mb: 2 }}>Apply to Join a Private Pool</Typography>
            <Typography variant="h6" sx={{ color: '#ccc', mb: 4 }}>
                Secure your spot in an exclusive presale. Contribute USDT and earn early access to $OSCAR rewards.
            </Typography>
            <TextField
                autoComplete="off"
                label="First Name"
                value={form.firstName}
                onChange={handleChange('firstName')}
                helperText="Enter your legal first name."
                error={errors.firstName}
                {...fieldProps}
            />
            <TextField
                autoComplete="off"
                label="Last Name"
                value={form.lastName}
                onChange={handleChange('lastName')}
                helperText="Enter your legal last name."
                error={errors.lastName}
                {...fieldProps}
            />
            <TextField
                autoComplete="off"
                fullWidth
                select
                label="Select Private Pool"
                value={form.poolName}
                onChange={handleChange('poolName')}
                error={errors.poolName}
                sx={{
                    mb: 3,
                    backgroundColor: '#1e1e1e',
                    '& .MuiInput-underline:before': {
                        borderBottomColor: '#efbc00',
                    },
                    '& .MuiInput-underline.Mui-focused:before': {
                        borderBottomColor: '#efbc00',
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#749d1a' },
                    '& .MuiMenuItem-root': { color: '#ccc' },
                    '& .MuiSelect-select': { color: '#ccc' },
                    '& .MuiSelect-icon': { color: '#ccc' }
                }}
                InputLabelProps={{ style: { color: '#ccc' } }}
            >
                {privatePools.map((pool) => (
                    <MenuItem key={pool.title} value={pool.title}>
                        {pool.title}
                    </MenuItem>
                ))}
            </TextField>

            <TextField
                autoComplete="off"
                type='number'
                label="Amount to Contribute"
                placeholder="Minimum $100"
                value={form.purchaseAmount}
                onChange={handleChange('purchaseAmount')}
                helperText="Enter the amount you wish to buy in USDC. Minimum $100."
                error={errors.purchaseAmount}
                {...fieldProps}
            />
            <TextField
                autoComplete="off"
                label="Twitter Username"
                placeholder="@yourname"
                value={form.twitter}
                onChange={handleChange('twitter')}
                helperText="Provide your Twitter handle (e.g., @yourname) for eligibility verification."
                error={errors.twitter}
                {...fieldProps}
            />
            <TextField
                autoComplete="off"
                label="Company (Optional)"
                value={form.company}
                onChange={handleChange('company')}
                helperText="(Optional) The name of the company or online community you represent."
                {...fieldProps}
            />
            <TextField
                autoComplete="off"
                label="Email Address (Optional)"
                value={form.email}
                onChange={handleChange('email')}
                helperText="Optional: For notifications and updates about your private pool purchase."
                {...fieldProps}
            />
            <TextField
                autoComplete="off"
                label="Message to the Oscar Team"
                placeholder="Share anything you'd like us to know"
                value={form.message}
                onChange={handleChange('message')}
                multiline
                minRows={4}
                {...fieldProps}
            />

            <Button type="submit" variant="contained" fullWidth sx={{ backgroundColor: '#749d1a', color: '#000', fontWeight: 600, fontSize: 20, py: 1.5, '&:hover': { backgroundColor: '#dbaa00' } }}>
                Submit Request
            </Button>

            <Backdrop sx={{ color: "#FFFFFF", zIndex: (theme) => theme.zIndex.drawer + 1, position: "absolute" }} open={isModalLoading}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Box>
    );
};

export default PrivatePoolParticipantForm; 